import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, X, Eye, EyeOff } from "lucide-react";
import { adminApi } from "@/lib/api";

/**
 * AdminResource — a schema-driven CRUD table for any collection.
 * `fields` is an array of {name, label, type ('text'|'textarea'|'number'|'bool'|'list'|'select'|'image'), options?}.
 */
export default function AdminResource({ collection, title, fields, defaults = {}, listColumns = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try { setItems(await adminApi.list(collection)); }
    catch (e) { toast.error("No se pudo cargar la lista"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [collection]);

  const openNew = () => {
    setEditing({ id: null });
    setForm({ publicado: true, orden: items.length, ...defaults });
  };

  const openEdit = (item) => {
    setEditing({ id: item.id });
    const base = {};
    fields.forEach((f) => {
      const val = item[f.name];
      if (f.type === "list") base[f.name] = Array.isArray(val) ? val.join("\n") : val || "";
      else base[f.name] = val ?? "";
    });
    base.publicado = item.publicado !== false;
    base.orden = item.orden ?? 0;
    setForm(base);
  };

  const close = () => { setEditing(null); setForm({}); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.name];
      if (f.type === "number") v = v === "" || v == null ? null : Number(v);
      if (f.type === "bool") v = !!v;
      if (f.type === "list") v = (v || "").split("\n").map((s) => s.trim()).filter(Boolean);
      payload[f.name] = v;
    });
    payload.publicado = form.publicado !== false;
    if (form.orden !== "" && form.orden != null) payload.orden = Number(form.orden);

    try {
      if (editing.id) await adminApi.update(collection, editing.id, payload);
      else await adminApi.create(collection, payload);
      toast.success("Guardado");
      close();
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al guardar");
    }
  };

  const togglePublish = async (item) => {
    try {
      await adminApi.update(collection, item.id, { publicado: !item.publicado });
      await load();
    } catch { toast.error("Error"); }
  };

  const remove = async (item) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    try { await adminApi.remove(collection, item.id); toast.success("Eliminado"); await load(); }
    catch { toast.error("Error"); }
  };

  const cols = listColumns || fields.slice(0, 2).map((f) => f.name);

  return (
    <div className="p-8 md:p-10" data-testid={`admin-resource-${collection}`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="eyebrow mb-2">— Contenido</div>
          <h1 className="font-serif text-4xl">{title}</h1>
        </div>
        <button onClick={openNew} className="kb-btn" data-testid="admin-new">
          <Plus className="w-4 h-4" /> Nuevo
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#4A4A4A]">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#4A4A4A] border border-dashed border-[#1A1A1A]/15 p-10 text-center">Sin registros aún.</div>
      ) : (
        <div className="border border-[#1A1A1A]/12 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#EFEBE2] text-left text-xs eyebrow">
              <tr>
                {cols.map((c) => <th key={c} className="px-4 py-3 font-medium">{c}</th>)}
                <th className="px-4 py-3 w-32">Publicado</th>
                <th className="px-4 py-3 w-40 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-[#1A1A1A]/10" data-testid={`admin-row-${item.id}`}>
                  {cols.map((c) => {
                    const v = item[c];
                    let display = v;
                    if (Array.isArray(v)) display = v.join(", ");
                    if (typeof v === "boolean") display = v ? "Sí" : "No";
                    const short = typeof display === "string" ? display.slice(0, 80) : display;
                    return <td key={c} className="px-4 py-3 align-top max-w-[260px] truncate">{String(short ?? "")}</td>;
                  })}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => togglePublish(item)}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${item.publicado ? "bg-[#1B3B2B] text-[#F9F6F0]" : "bg-[#1A1A1A]/10 text-[#4A4A4A]"}`}
                    >
                      {item.publicado ? <><Eye className="w-3 h-3" /> Publicado</> : <><EyeOff className="w-3 h-3" /> Oculto</>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(item)} className="text-xs underline mr-4">Editar</button>
                    <button onClick={() => remove(item)} className="text-xs text-red-700" data-testid={`admin-delete-${item.id}`}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer / modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={close}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl h-full bg-[#F9F6F0] overflow-y-auto p-8"
            data-testid="admin-editor"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">{editing.id ? "Editar" : "Nuevo"} · {title}</h2>
              <button type="button" onClick={close} className="h-9 w-9 rounded-full border border-[#1A1A1A]/20 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            <div className="space-y-5">
              {fields.map((f) => (
                <FieldRenderer key={f.name} field={f} value={form[f.name] ?? ""} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />
              ))}
              <div className="flex items-center gap-4 pt-4 border-t border-[#1A1A1A]/10">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.publicado !== false} onChange={(e) => setForm((s) => ({ ...s, publicado: e.target.checked }))} />
                  Publicado
                </label>
                <label className="flex items-center gap-2 text-sm">
                  Orden <input type="number" value={form.orden ?? 0} onChange={(e) => setForm((s) => ({ ...s, orden: e.target.value }))} className="w-20 border-b border-[#1A1A1A]/25 bg-transparent" />
                </label>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <button type="submit" className="kb-btn"><Save className="w-4 h-4" /> Guardar</button>
              <button type="button" onClick={close} className="kb-btn ghost">Cancelar</button>
              {editing.id && (
                <button type="button" onClick={() => remove({ id: editing.id })} className="ml-auto text-xs text-red-700 flex items-center gap-2">
                  <Trash2 className="w-3 h-3" /> Eliminar
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  const common = "w-full bg-transparent border-b border-[#1A1A1A]/25 py-2 focus:border-[#1B3B2B] focus:outline-none text-sm";
  if (field.type === "textarea" || field.type === "list") {
    return (
      <div>
        <label className="eyebrow block mb-1">{field.label} {field.type === "list" && <span className="normal-case text-[10px] tracking-normal">(uno por línea)</span>}</label>
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={field.rows || 4} className={`${common} resize-y`} />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <label className="eyebrow block mb-1">{field.label}</label>
        <select value={value} onChange={(e) => onChange(e.target.value)} className={common}>
          <option value="">—</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "bool") {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {field.label}
      </label>
    );
  }
  return (
    <div>
      <label className="eyebrow block mb-1">{field.label}</label>
      <input type={field.type === "number" ? "number" : "text"} value={value} onChange={(e) => onChange(e.target.value)} className={common} />
    </div>
  );
}
